import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Camera, Sparkles, MessageCircle, History, Check, Hand, Star, Book, LogIn } from 'lucide-react';

const TutorialPage = () => {
  const navigate = useNavigate();

  const steps = [
    {
      number: 1,
      icon: LogIn,
      title: "Créez votre compte gratuit",
      description: "Inscrivez-vous en quelques secondes avec votre email ou via Google. Votre compte vous permet de sauvegarder vos lectures et d'accéder à toutes les fonctionnalités.",
      image: "https://images.pexels.com/photos/5701329/pexels-photo-5701329.jpeg?auto=compress&cs=tinysrgb&w=600&h=300&fit=crop",
      tips: [
        "L'inscription est 100% gratuite",
        "Connexion possible via Google",
        "Vos données restent privées et sécurisées"
      ]
    },
    {
      number: 2,
      icon: Camera,
      title: "Capturez vos mains",
      description: "Choisissez entre l'analyse palmaire (2 images) ou complète recto-verso (4 images). Utilisez la caméra ou uploadez des photos.",
      image: "https://images.pexels.com/photos/8058729/pexels-photo-8058729.jpeg?auto=compress&cs=tinysrgb&w=600&h=300&fit=crop",
      tips: [
        "Prenez les photos dans un endroit bien éclairé",
        "Assurez-vous que toutes les lignes sont visibles",
        "Gardez la main bien à plat"
      ]
    },
    {
      number: 3,
      icon: Hand,
      title: "Sélectionnez vos thèmes",
      description: "Choisissez les aspects de votre vie à explorer : amour, carrière, santé, longévité, finances, créativité, spiritualité.",
      image: "https://images.unsplash.com/photo-1743612266425-40b58038397f?w=600&h=300&fit=crop",
      tips: [
        "Vous pouvez sélectionner plusieurs thèmes",
        "Plus vous choisissez de thèmes, plus l'analyse sera complète",
        "Commencez par 2-3 thèmes pour votre première lecture"
      ]
    },
    {
      number: 4,
      icon: Sparkles,
      title: "Recevez votre analyse",
      description: "Notre IA analyse vos images et génère une lecture personnalisée basée sur la chiromancie traditionnelle et les observations anatomiques.",
      image: "https://images.unsplash.com/photo-1459624470348-67edb45d81b1?w=600&h=300&fit=crop",
      tips: [
        "L'analyse prend environ 30 secondes",
        "Main gauche = potentiel inné",
        "Main droite = évolution et choix",
        "Le mode recto-verso inclut l'analyse physique"
      ]
    },
    {
      number: 5,
      icon: MessageCircle,
      title: "Discutez avec Aria",
      description: "Posez toutes vos questions à Aria, notre guide en chiromancie. Elle répondra en se basant sur votre lecture spécifique ou sur vos questions générales.",
      image: "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=600&h=300&fit=crop",
      tips: [
        "Demandez des précisions sur un insight",
        "Obtenez des conseils personnalisés",
        "Explorez des aspects non couverts",
        "Pas de limite de questions !"
      ]
    },
    {
      number: 6,
      icon: History,
      title: "Consultez votre historique",
      description: "Toutes vos lectures sont sauvegardées. Comparez-les dans le temps pour voir votre évolution personnelle.",
      image: "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=600&h=300&fit=crop",
      tips: [
        "Vos lectures restent privées",
        "Comparez plusieurs lectures pour voir les changements",
        "Ajoutez vos insights préférés aux favoris",
        "Programmez des rappels pour de futures lectures"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 py-12 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 rounded-full hover:bg-secondary/50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-4">
            Comment utiliser Lumina
          </h1>
          <p className="text-muted-foreground text-base md:text-lg">
            Découvrez comment obtenir votre lecture de main personnalisée en 6 étapes simples
          </p>
        </motion.div>

        {/* Steps */}
        <div className="space-y-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="relative"
            >
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="absolute left-8 top-20 bottom-0 w-0.5 bg-gradient-to-b from-primary to-secondary -z-10 hidden md:block" />
              )}

              <div className="bg-card rounded-2xl border border-border shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                <div className="md:flex">
                  {/* Image */}
                  <div className="md:w-1/3 relative">
                    <img 
                      src={step.image} 
                      alt={step.title}
                      className="w-full h-48 md:h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20 md:bg-gradient-to-t md:from-black/40 md:to-transparent" />
                    <div className="absolute top-4 left-4 w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-heading text-lg shadow-lg">
                      {step.number}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="md:w-2/3 p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-secondary/30 flex items-center justify-center">
                        <step.icon className="h-5 w-5 text-primary" />
                      </div>
                      <h2 className="font-heading text-xl md:text-2xl">{step.title}</h2>
                    </div>
                    <p className="text-muted-foreground mb-4 leading-relaxed">
                      {step.description}
                    </p>

                    {/* Tips */}
                    <div className="bg-secondary/20 p-4 rounded-xl">
                      <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                        <Star className="h-4 w-4 text-primary" />
                        Conseils :
                      </p>
                      <ul className="space-y-2">
                        {step.tips.map((tip, tipIndex) => (
                          <li key={tipIndex} className="text-sm text-muted-foreground flex items-start gap-2">
                            <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 grid md:grid-cols-2 gap-6"
        >
          <div className="bg-orange-50 p-6 rounded-2xl border border-orange-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Book className="h-5 w-5 text-orange-600" />
              </div>
              <h3 className="font-heading text-lg text-orange-900">Base de connaissances</h3>
            </div>
            <p className="text-sm text-orange-800 mb-4">
              Apprenez la chiromancie : histoire, lignes principales, monts de la paume et interprétations.
            </p>
            <Button
              onClick={() => navigate('/knowledge')}
              variant="outline"
              className="rounded-full border-orange-300 text-orange-700 hover:bg-orange-100"
            >
              Découvrir
            </Button>
          </div>
          
          <div className="bg-purple-50 p-6 rounded-2xl border border-purple-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <MessageCircle className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="font-heading text-lg text-purple-900">Chat éducatif</h3>
            </div>
            <p className="text-sm text-purple-800 mb-4">
              Posez vos questions à Aria sur la chiromancie, même sans avoir fait de lecture.
            </p>
            <Button
              onClick={() => navigate('/chat-general')}
              variant="outline"
              className="rounded-full border-purple-300 text-purple-700 hover:bg-purple-100"
            >
              Poser une question
            </Button>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-12 text-center"
        >
          <div className="bg-gradient-to-r from-primary to-primary/80 p-8 rounded-3xl shadow-xl">
            <h3 className="font-heading text-2xl text-white mb-4">
              Prêt à découvrir votre destinée ?
            </h3>
            <p className="text-white/90 mb-6">
              Commencez votre première lecture maintenant
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                data-testid="start-reading-btn"
                onClick={() => navigate('/scan')}
                size="lg"
                className="rounded-full bg-white text-primary hover:bg-white/90 px-8 py-6 text-base font-semibold shadow-lg"
              >
                <Camera className="mr-2 h-5 w-5" />
                Commencer ma lecture
              </Button>
              <Button
                data-testid="chat-general-btn"
                onClick={() => navigate('/chat-general')}
                variant="outline"
                size="lg"
                className="rounded-full border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-base font-semibold"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Poser une question
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TutorialPage;
